import { Box, Grid, Paper, SxProps, Tooltip, useTheme } from "@mui/material";
import { Deriv } from "../../../interfaces/dtos/deriv";
import { useState } from "react";
import { ScheduleDerivsListItem } from "./ScheduleDerivsListItem";
import { commonColours } from "../../shared/commonConstants";

interface ScheduleDerivsListProps {
    eventDerivs: Deriv[];
}

export const suspendedText = 'Suspended';

export const ScheduleDerivsList = ({
    eventDerivs
}: ScheduleDerivsListProps) => {

    const theme = useTheme();
    const [selectedMarketGroupKey, setSelectedMarketGroupKey] = useState<string | undefined>(undefined);

    // Get the unique market types
    const marketGroups = [...new Set(eventDerivs.map(ed => ed.calculated?.groupKey ?? ''))];

    const getOrderedEventDeriv = (marketGroupKey: string) => {
        return eventDerivs
            .filter(ed => ed.calculated?.groupKey === marketGroupKey)
            .toSorted((a, b) => ((a.version > b.version ? -1 : 1)));
    }

    const getMarketName = (marketGroupKey: string) => {
        const eventDerivsOrdered = getOrderedEventDeriv(marketGroupKey);
        return eventDerivsOrdered[0].marketName ?? '';
    }

    const isMarketSuspended = (marketGroupKey: string) => {
        // Get the latest version status
        const eventDerivsOrdered = getOrderedEventDeriv(marketGroupKey);
        return eventDerivsOrdered[0].calculated?.isSuspended;
    }

    const getMarketTypeDesc = (marketGroupKey: string) => {
        const isSuspended = isMarketSuspended(marketGroupKey);
        const eventDerivsOrdered = getOrderedEventDeriv(marketGroupKey);
        const tooltip = isSuspended ?
            `${suspendedText} - ${eventDerivsOrdered[0].calculated?.suspendedReason}` :
            undefined;
        return (
            <Tooltip title={tooltip}
                slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] } }}
            >
                <Box sx={{ whiteSpace: 'nowrap' }}>
                    <span style={{ color: isSuspended ? commonColours(theme).suspended : theme.palette.success.dark }}>
                        {getMarketName(marketGroupKey)}
                    </span>
                    {isSuspended &&
                        <span style={{ color: commonColours(theme).suspended, opacity: '0.4', paddingLeft: '10px', fontSize: '12px' }}>
                            {suspendedText}
                        </span>
                    }
                </Box>
            </Tooltip>
        )
    }

    const handleSelected = (marketGroupKey: string) => {
        setSelectedMarketGroupKey(marketGroupKey);
    }

    const maxCharCount = Math.max(...marketGroups.map(mg =>
        (getMarketName(mg).length * 3) +
        (isMarketSuspended(mg) ? (suspendedText.length * 2) : 0) // the status is a smaller font so counts as less width
    ));
    const itemWidth = 4 * maxCharCount;

    const containerSx: SxProps = { padding: 2, border: `1px solid ${theme.palette.grey[400]}`, borderRadius: '4px', backgroundColor: theme.palette.grey[200] }
    const containerGridSx: SxProps = { justifyContent: 'center' }
    const itemGridSx = (marketGroupKey: string): SxProps => ({
        width: `${itemWidth}px`,
        "& :hover": { backgroundColor: selectedMarketGroupKey === marketGroupKey ? undefined : theme.palette.grey[200] }
    })
    const itemSx = (marketGroupKey: string): SxProps => ({
        padding: '4px 10px', cursor: 'pointer',
        backgroundColor: selectedMarketGroupKey === marketGroupKey ? theme.palette.primary.light : undefined,
    })

    return (
        <>
            <Box id='deriv-group' sx={containerSx}>
                <Grid container rowSpacing={1} columnSpacing={2} sx={containerGridSx}>
                    {marketGroups.map((marketGroupKey) =>
                        <Grid
                            key={marketGroupKey}
                            sx={itemGridSx(marketGroupKey)}
                        >
                            <Paper
                                elevation={4}
                                id={`deriv-group-${marketGroupKey}`}
                                sx={itemSx(marketGroupKey)}
                                onClick={() => handleSelected(marketGroupKey)}
                            >
                                {getMarketTypeDesc(marketGroupKey)}
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Box >
            {
                selectedMarketGroupKey &&
                <ScheduleDerivsListItem
                    eventDerivs={getOrderedEventDeriv(selectedMarketGroupKey)}
                />
            }
        </>
    )
}
